'use client';

import { type ReactNode } from 'react';
import { extractListItems, getImageUrlForVariant, stripHtml } from '@/lib/homepage-mappers';
import type { AboutSafetyQualitySection } from '@/types/aboutus';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { cn } from '@/utils/cn';

type CommitmentCard = {
  id: string;
  title: string;
  titleClassName?: string;
  imageSrc: string;
  imageAlt: string;
  bullets: ReactNode[];
  tags: string[];
  tagTone?: 'red' | 'neutral';
};

const FALLBACK_CARDS: CommitmentCard[] = [
  {
    id: 'safety',
    title: 'Safety',
    titleClassName: 'text-[#ed1c22]',
    imageSrc: '/images/about_new.png',
    imageAlt: 'Safety practices at Wonder Cement plant operations',
    bullets: [
      <>
        All Wonder Cement operating locations organise a{' '}
        <strong className="font-semibold text-[#111]">
          safety gate meeting on the 1st working day
        </strong>{' '}
        of each month to reinforce the best safety practices among employees.
      </>,
    ],
    tags: ['Digital Systems', 'Risk Management', 'Analytics-Driven', 'Automated Operations'],
    tagTone: 'red',
  },
  {
    id: 'quality',
    title: 'Quality',
    titleClassName: 'text-[#ed1c22]',
    imageSrc: '/images/quality.webp',
    imageAlt: 'Wonder Cement quality control and precision manufacturing',
    bullets: [
      <>
        <strong className="font-semibold text-[#111]">POLAB &amp; Robotic Arm</strong> — automated
        lab sampling and analysis for real-time mix consistency.
      </>,
    ],
    tags: ['POLAB', 'Robotic Arm', 'CBA / CNA', 'CGGT', 'HES'],
    tagTone: 'red',
  },
];

function buildCardsFromZone(zone?: AboutSafetyQualitySection): CommitmentCard[] {
  if (!zone?.safetyQualityWrap?.length) return FALLBACK_CARDS;

  return zone.safetyQualityWrap.map((wrap, index) => {
    const pill = wrap.imagewithPill?.[0];
    const listItem = wrap.list?.[0];
    const bullets = extractListItems(listItem?.listContent);
    const imageSrc =
      getImageUrlForVariant(pill?.media, 'large') ||
      getImageUrlForVariant(pill?.media, 'medium') ||
      FALLBACK_CARDS[index]?.imageSrc ||
      '/images/about_new.png';

    return {
      id: `commitment-${wrap.id}`,
      title: listItem?.listTitle || FALLBACK_CARDS[index]?.title || `Commitment ${index + 1}`,
      titleClassName: 'text-[#ed1c22]',
      imageSrc,
      imageAlt: pill?.media?.alternativeText || listItem?.listTitle || 'Safety and quality',
      bullets: bullets.length
        ? bullets.map((bullet) => (
            <span key={bullet} dangerouslySetInnerHTML={{ __html: bullet }} />
          ))
        : (FALLBACK_CARDS[index]?.bullets ?? []),
      tags:
        wrap.cardFooter?.map((item) => item.heading).filter(Boolean) ??
        FALLBACK_CARDS[index]?.tags ??
        [],
      tagTone: 'red' as const,
    };
  });
}

function CommitmentKeywords({ tags, tone }: { tags: string[]; tone: 'red' | 'neutral' }) {
  const tagClassName = cn(
    'text-[10px] font-medium uppercase leading-relaxed tracking-[0.14em]',
    tone === 'red' ? 'text-[#ed1c22]/75' : 'text-[#999]',
  );

  return (
    <div className="pointer-events-none select-none" aria-label={tags.join(', ')}>
      <div className="flex flex-wrap gap-2 sm:hidden">
        {tags.map((tag) => (
          <span key={tag} className={tagClassName}>
            {tag}
          </span>
        ))}
      </div>

      <p className={cn('hidden leading-relaxed sm:block', tagClassName)}>
        {tags.map((tag, index) => (
          <span key={tag}>
            {index > 0 ? (
              <span
                className="mx-2 font-normal normal-case tracking-normal text-[#ccc]"
                aria-hidden
              >
                ·
              </span>
            ) : null}
            {tag}
          </span>
        ))}
      </p>
    </div>
  );
}

interface AboutSafetyQualityCommitmentsProps {
  zone?: AboutSafetyQualitySection;
}

export default function AboutSafetyQualityCommitments({
  zone,
}: AboutSafetyQualityCommitmentsProps) {
  const commitmentCards = buildCardsFromZone(zone);
  const headingParts = zone?.heading?.length
    ? zone.heading
    : [
        { id: 1, text: 'Safety', textColor: null },
        { id: 2, text: '&', textColor: '#ed1c22' },
        { id: 3, text: 'Quality', textColor: null },
      ];
  const subtitle =
    stripHtml(zone?.subHeading) || 'Built into every process, every product, every day.';

  return (
    <section className="bg-[#f3f3f3]" aria-label="Safety and Quality">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-10">
        <div className="relative pb-6 sm:pb-8">
          <div className="flex flex-col gap-4 sm:gap-5">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-[#111] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
                {headingParts.map((part) => (
                  <span
                    key={part.id}
                    style={part.textColor ? { color: part.textColor } : undefined}
                  >
                    {part.text}
                    {part.text === '&' ? ' ' : ''}
                  </span>
                ))}
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-[#888] lg:text-[15px]">
              {subtitle}
            </p>
          </div>
          <div
            className="absolute bottom-0 left-0 right-0 h-px origin-left bg-[#d9d9d9]"
            aria-hidden
          />
        </div>

        <div className="mt-6 grid items-stretch gap-6 lg:mt-8 lg:grid-cols-2 lg:gap-7">
          {commitmentCards.map((card) => (
            <article
              key={card.id}
              className="group flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-transparent bg-white shadow-[0_12px_40px_-12px_rgba(15,23,42,0.12)] transition-all duration-500 ease-out hover:-translate-y-2 hover:border-[#ed1c22]/20 hover:shadow-[0_24px_48px_-12px_rgba(237,28,34,0.18)]"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <CmsImage
                  src={card.imageSrc}
                  alt={card.imageAlt}
                  fill
                  className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  fallbackSrc={MEDIA_PLACEHOLDER}
                />
              </div>

              <div className="flex min-h-0 flex-1 flex-col px-5 py-6 sm:px-6 sm:py-7">
                <h3
                  className={cn(
                    'text-2xl font-bold tracking-tight transition-colors duration-300 group-hover:text-[#ed1c22] sm:text-[1.75rem]',
                    card.titleClassName,
                  )}
                >
                  {card.title}
                </h3>

                <div className="mt-5 flex min-h-0 flex-1 flex-col">
                  <ul className="mb-5 space-y-4">
                    {card.bullets.map((bullet, bulletIndex) => (
                      <li key={bulletIndex} className="flex gap-3">
                        <span
                          className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#ed1c22]"
                          aria-hidden
                        />
                        <p className="text-sm leading-[1.75] text-[#666] sm:text-[15px] [&_strong]:font-semibold [&_strong]:text-[#111]">
                          {bullet}
                        </p>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto border-t border-[#ececec] pt-4">
                    <CommitmentKeywords tags={card.tags} tone={card.tagTone ?? 'neutral'} />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
